import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  School as EducationIcon,
} from '@mui/icons-material';
import { useTranslate, useDataProvider, useNotify, useRefresh } from 'react-admin';
import { DocumentUpload } from '../DocumentUpload';
import { FileViewerDialog } from '../FileViewerDialog';

interface DetailedEducationStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  onClose: () => void;
  existingData?: any;
  driver?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const DetailedEducationStep: React.FC<DetailedEducationStepProps> = ({
  onNext,
  onBack,
  onClose,
  existingData,
  driver,
  isFirstStep,
  isLastStep
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // State for education records and dialog
  const [educationRecords, setEducationRecords] = useState([]);
  const [educationDialogOpen, setEducationDialogOpen] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);

  // State for education schools
  const [educationSchools, setEducationSchools] = useState([]);
  const [educationSchoolsLoading, setEducationSchoolsLoading] = useState(false);

  // State for file viewer
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);

  // Form state for the dialog
  const [educationForm, setEducationForm] = useState({
    drivingSchool: '',
    programTitle: '',
    completedDate: '',
    hasDocument: false,
    documentFileId: null,
    documentFile: null
  });

  // Load education records when driver is available
  useEffect(() => {
    if (driver?.education) {
      setEducationRecords(driver.education);
    } else if (driver?.id) {
      loadEducationRecords();
    }
  }, [driver?.id, driver?.education]);

  // Load education schools when component mounts
  useEffect(() => {
    const fetchEducationSchools = async () => {
      setEducationSchoolsLoading(true);
      try {
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';

        const response = await fetch(`${baseUrl}/code-tables/education-schools`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            ...(tenantsEnabled && { 'x-tenant-id': localStorage.getItem('tenantId') })
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch education schools`);
        }
        const data = await response.json();
        setEducationSchools(data);
      } catch (error) {
        console.error('Error fetching education schools:', error);
        notify('Failed to load education schools', { type: 'error' });
        setEducationSchools([]);
      } finally {
        setEducationSchoolsLoading(false);
      }
    };
    fetchEducationSchools();
  }, []);

  const loadEducationRecords = async () => {
    try {
      const result = await dataProvider.getOne('drivers', { id: driver.id });
      if (result.data?.education) {
        setEducationRecords(result.data.education);
      }
    } catch (error) {
      console.error('Error loading education records:', error);
      setEducationRecords([]);
    }
  };

  const handleEditEducation = (education: any) => {
    setEditingEducation(education);
    setEducationForm({
      drivingSchool: education.educationSchoolId ? education.educationSchoolId.toString() : '',
      programTitle: education.programTitle || '',
      completedDate: education.completedDate ? education.completedDate.split('T')[0] : '',
      hasDocument: !!education.fileId,
      documentFileId: education.fileId || null,
      documentFile: education.file || null
    });
    setEducationDialogOpen(true);
  };

  const handleDeleteEducation = async (educationId: number) => {
    if (window.confirm('Are you sure you want to delete this education record?')) {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';

      try {
        const response = await fetch(`${baseUrl}/drivers/education/${educationId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            ...(tenantsEnabled && { 'x-tenant-id': localStorage.getItem('tenantId') })
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to delete education`);
        }
        notify('Education record deleted successfully', { type: 'success' });
        loadEducationRecords();
        // Refresh the page to ensure data is updated
        window.location.reload();
      } catch (error) {
        console.error('Error deleting education record:', error);
        notify('Failed to delete education record', { type: 'error' });
      }
    }
  };

  const openFileViewer = (file: any) => {
    setViewingFile(file);
    setFileViewerOpen(true);
  };

  const resetEducationForm = () => {
    setEducationForm({
      drivingSchool: '',
      programTitle: '',
      completedDate: '',
      hasDocument: false,
      documentFileId: null,
      documentFile: null
    });
    setEditingEducation(null);
  };

  const handleSaveEducation = async () => {
    try {
      if (editingEducation) {
        await dataProvider.update('drivers/education', {
          id: editingEducation.id,
          data: {
            educationSchoolId: educationForm.drivingSchool ? parseInt(educationForm.drivingSchool) : null,
            programTitle: educationForm.programTitle,
            completedDate: educationForm.completedDate || null,
            status: 'COMPLETED',
            fileId: educationForm.documentFileId
          }
        });
        notify('Education record updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('drivers/education', {
          data: {
            driverId: driver.id,
            educationSchoolId: educationForm.drivingSchool ? parseInt(educationForm.drivingSchool) : null,
            programTitle: educationForm.programTitle,
            completedDate: educationForm.completedDate || null,
            status: 'COMPLETED',
            fileId: educationForm.documentFileId
          }
        });
        notify('Education record added successfully', { type: 'success' });
      }
      setEducationDialogOpen(false);
      resetEducationForm();
      loadEducationRecords();
      // Refresh the page to ensure data is updated
      window.location.reload();
    } catch (error) {
      notify(`Failed to ${editingEducation ? 'update' : 'add'} education record`, { type: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'normal', mb: 4, textAlign: 'center' }}>
        <EducationIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        Education History
      </Typography>

      {/* Education Records Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Education Records</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => {
              resetEducationForm();
              setEducationDialogOpen(true);
            }}
          >
            Add Education
          </Button>
        </Box>

        {educationRecords.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>School</TableCell>
                <TableCell>Program</TableCell>
                <TableCell>Completed Date</TableCell>
                <TableCell>Document</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {educationRecords.map((edu: any) => (
                <TableRow key={edu.id}>
                  <TableCell>
                    {edu.educationSchool?.name || 'N/A'}
                  </TableCell>
                  <TableCell>{edu.programTitle || 'N/A'}</TableCell>
                  <TableCell>
                    {edu.completedDate ? new Date(edu.completedDate).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {edu.file ? (
                      <Button
                        size="small"
                        onClick={() => openFileViewer(edu.file)}
                        variant="outlined"
                      >
                        View Document
                      </Button>
                    ) : (
                      'No document'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEditEducation(edu)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteEducation(edu.id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No education records found. Click "Add Education" to get started.
          </Typography>
        )}
      </Paper>

      {/* Action Buttons */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 4
      }}>
        {!isFirstStep && (
          <Button
            variant="outlined"
            size="large"
            onClick={onBack}
            sx={{ px: 4, py: 1.5 }}
          >
            Back
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          size="large"
          onClick={() => onNext({})}
          sx={{ px: 4, py: 1.5 }}
        >
          {isLastStep ? 'Complete' : 'Next'}
        </Button>
      </Box>

      {/* Add/Edit Education Dialog */}
      <Dialog
        open={educationDialogOpen}
        onClose={() => setEducationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingEducation ? 'Edit Education' : 'Add Education'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Driving School
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Select School</InputLabel>
                  <Select
                    value={educationForm.drivingSchool}
                    onChange={(e) => setEducationForm({
                      ...educationForm,
                      drivingSchool: e.target.value
                    })}
                    label="Select School"
                    disabled={educationSchoolsLoading}
                  >
                    {educationSchools.map((school: any) => (
                      <MenuItem key={school.id} value={school.id.toString()}>
                        {school.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Program Title
                </Typography>
                <MuiTextField
                  size="small"
                  fullWidth
                  value={educationForm.programTitle}
                  onChange={(e) => setEducationForm({
                    ...educationForm,
                    programTitle: e.target.value
                  })}
                  placeholder=""
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Completed Date
                </Typography>
                <MuiTextField
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={educationForm.completedDate}
                  onChange={(e) => setEducationForm({
                    ...educationForm,
                    completedDate: e.target.value
                  })}
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Education Document (Optional)
                </Typography>

                {/* Show existing file if editing */}
                {editingEducation && educationForm.documentFile && (
                  <Box sx={{
                    mb: 2,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: '#f9f9f9'
                  }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => {
                            if (educationForm.documentFile) {
                              openFileViewer(educationForm.documentFile);
                            }
                          }}
                        >
                          {educationForm.documentFile.name || 'Education Document'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Current file (click to view)
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          setEducationForm({
                            ...educationForm,
                            documentFileId: null,
                            documentFile: null,
                            hasDocument: false
                          });
                        }}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </Box>
                )}

                <DocumentUpload
                  documentType="education"
                  driverId={driver?.id}
                  onUploadSuccess={(fileData) => {
                    setEducationForm({
                      ...educationForm,
                      documentFileId: fileData.id,
                      documentFile: fileData,
                      hasDocument: true
                    });
                    notify('Education document uploaded successfully', { type: 'success' });
                  }}
                  onUploadError={(error) => {
                    notify(`Upload failed: ${error}`, { type: 'error' });
                  }}
                  acceptTypes="image/*,.pdf,.doc,.docx"
                  maxSizeMB={10}
                />
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setEducationDialogOpen(false)}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Close
          </Button>
          {editingEducation && (
            <Button
              onClick={() => {
                handleDeleteEducation(editingEducation.id);
                setEducationDialogOpen(false);
                resetEducationForm();
              }}
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ minWidth: 100 }}
            >
              Delete
            </Button>
          )}
          <Button
            onClick={handleSaveEducation}
            variant="contained"
            sx={{ minWidth: 100 }}
          >
            {editingEducation ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <FileViewerDialog
        open={fileViewerOpen}
        onClose={() => setFileViewerOpen(false)}
        file={viewingFile}
      />
    </Box>
  );
};